<template>
  <div v-if="competitor" class="flex flex-col h-full pb-12 overflow-hidden">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedCompetitor?.code ? modifiedCompetitor.code : '-'"
      link-name="competitors"
      :is-modified="isModified"
      :is-edit-active="competitor.isFromManager || false"
      :mode="mode"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer :layout="isHorizontalLayout ? 'vertical' : 'horizontal'" :minSize="300">
      <template #headerTitle v-if="isHorizontalLayout">
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in section1Tabs"
              :key="tab.name"
              @click="selectedTab = tab"
              class="py-1.5 cursor-pointer"
              :class="[
                selectedTab.name === tab.name ? 'border-b-2 border-theme-500' : '',
                tab.validator &&
                tab.validator.some((item) => {
                  return $v[item].required.$invalid;
                })
                  ? 'text-red-500 !border-red-500'
                  : '',
              ]">
              <p :class="isInvalidTab(tab.name) === true ? 'text-red-500' : ''">
                {{ tab.label }}
              </p>
            </li>
          </ul>
        </nav>
      </template>
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <component
              :is="currentComponent"
              v-model="modifiedCompetitor"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout"
              :key="competitor?.id"
              :code-error-message="$v.code.required.$invalid ? $v.code.required.$message : ''"
              :competitor-type-error-message="
                $v.typeId.required.$invalid ? $v.typeId.required.$message : ''
              "
              :trader-error-message="
                $v.traderId.required.$invalid ? $v.traderId.required.$message : ''
              " />
          </PerfectScrollbar>
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <BaseAccordion
              v-for="tab in section1Tabs"
              :key="tab.name"
              :name="tab.label"
              :error="
                tab.validator &&
                tab.validator.some((item) => {
                  return $v[item].required.$invalid;
                })
              ">
              <CompetitorDetailsTabProperties
                v-if="tab.name === 'properties'"
                v-model="modifiedCompetitor"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout"
                :code-error-message="$v.code.required.$invalid ? $v.code.required.$message : ''"
                :competitor-type-error-message="
                  $v.typeId.required.$invalid ? $v.typeId.required.$message : ''
                " />
              <CompetitorDetailsTabTrader
                v-else-if="tab.name === 'trader'"
                v-model="modifiedCompetitor"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout"
                :trader-error-message="
                  $v.traderId.required.$invalid ? $v.traderId.required.$message : ''
                " />
            </BaseAccordion>
          </PerfectScrollbar>
        </template>
      </template>

      <template #detailsContent>
        <BaseTable
          :is-add-new-hidden="true"
          :total-records="competitorMaterialsCount"
          :headers="competitorMaterialsTableHeaders"
          :list="competitorMaterials"
          hide-grouping
          @refresh-data="onRefreshData"
          table-id="competitorMaterialsByCompetitor" />
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseAccordion from '@/components/common/BaseAccordion.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import type { Competitor } from '@/types/Traders';
import { getCompetitorById, addOrEditCompetitor } from '@/services/api/traders';
import CompetitorDetailsTabProperties from '@/components/traders/CompetitorDetailsTabProperties.vue';
import CompetitorDetailsTabTrader from '@/components/traders/CompetitorDetailsTabTrader.vue';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import type { CompetitorMaterial } from '@/types/Materials';
import { getCompetitorMaterialsWithConditions } from '@/services/api/materials';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import type { TableFilterData } from '@/types/Base';

const { getUsersLookup } = useLookupsStore();
const { users } = storeToRefs(useLookupsStore());
const { t: $t } = useI18n();
const competitor = ref<Competitor>();
const competitorMaterials = ref<CompetitorMaterial[]>([]);
const competitorMaterialsCount = ref<number>(0);
const modifiedCompetitor = ref<Competitor>();
const isHorizontalLayout = ref(true);
const scrollbarId = ref<PerfectScrollbarExpose>();
const mode = ref<'view' | 'edit'>('view');
const route = useRoute();
const router = useRouter();

const isModified = ref<boolean>(false);
const confirmModalActive = ref(false);

const section1Tabs = ref([
  {
    name: 'properties',
    label: $t('competitor.properties'),
    component: markRaw(CompetitorDetailsTabProperties),
    validator: ['code', 'typeId'],
  },
  {
    name: 'trader',
    label: $t('competitor.trader'),
    component: markRaw(CompetitorDetailsTabTrader),
    validator: ['traderId'],
  },
]);
const selectedTab = ref(section1Tabs.value[0]);

const currentComponent = computed(() => {
  const selected = section1Tabs.value.find((tab) => tab.name === selectedTab.value.name);
  return selected ? selected.component : null;
});

const competitorMaterialsTableHeaders = [
  { value: 'boid', text: $t('baseObject.boid'), type: 'text', active: true },
  { value: 'competitor.code', text: $t('material.competitor'), type: 'text', active: true },
  {
    value: 'materialAlterCode.alterCode.name',
    text: $t('groups.alterCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCode.measurementUnit.name',
    text: $t('material.measurementUnit'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isDefault', text: $t('material.default'), type: 'check', active: true },
];

watch(
  () => modifiedCompetitor.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(competitor.value);
    validationModel.value.code = (modifiedCompetitor.value?.code as string) || '';
    validationModel.value.typeId = (modifiedCompetitor.value?.competitorTypeId as string) || '';
    validationModel.value.traderId = (modifiedCompetitor.value?.traderId as string) || '';

    $v.value.$touch();
  },
  { deep: true }
);

onMounted(async () => {
  const res = await getCompetitorById(route.params.id as string);
  if (res) {
    competitor.value = res as Competitor;
    await populateUsersFromLookups();
    onRefreshData({});
    mode.value = 'view';
  } else {
    competitor.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      traderId: '',
      competitorTypeId: '',
    };
    competitorMaterials.value = [];
    mode.value = 'edit';
  }
  modifiedCompetitor.value = deepCopy<Competitor>(competitor.value);
});

async function populateUsersFromLookups() {
  await getUsersLookup();
  if (competitor.value?.createUserId)
    competitor.value.createUser = users.value.find((x) => x.id == competitor.value?.createUserId);
  if (competitor.value?.updateUserId)
    competitor.value.updateUser = users.value.find((x) => x.id == competitor.value?.updateUserId);
}

const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!competitor.value?.createdDate) {
    router.push('/competitors');
  }
  mode.value = 'view';
}

function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (competitor.value) modifiedCompetitor.value = deepCopy<Competitor>(competitor.value);
  if (!competitor.value?.createdDate || isBackMode.value) {
    router.push('/competitors');
  }
}

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  mode.value = 'view';
  if (modifiedCompetitor.value) await addOrEditCompetitor(modifiedCompetitor.value);
  if (modifiedCompetitor.value) competitor.value = deepCopy<Competitor>(modifiedCompetitor.value);
  isModified.value = false;
}

const validationModel = ref({
  code: modifiedCompetitor.value?.code || '',
  typeId: modifiedCompetitor.value?.competitorTypeId || '',
  traderId: modifiedCompetitor.value?.traderId || '',
});
const rules = computed(() => ({
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  typeId: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  traderId: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));
const $v = useVuelidate(rules, validationModel, { $lazy: true });

function isInvalidTab(tabName: string): true | false {
  if (tabName == 'properties') return $v.value.code.$invalid || $v.value.typeId.$invalid;
  return $v.value.traderId.$invalid;
}

async function onRefreshData(data: TableFilterData) {
  const sortColumn =
    data.column !== undefined ? competitorMaterialsTableHeaders[data.column]?.value : 'boid';
  const res = await getCompetitorMaterialsWithConditions({
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 20,
    query: data.query,
    sortColumn,
    sortAscending: data.order == 'asc',
    competitorId: route.params.id as string,
  });
  if (res) {
    competitorMaterials.value = res.data;
    competitorMaterialsCount.value = res.count;
  }
}
</script>
